import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Trash2, Loader2, Database, Radio, X, FilterX } from "lucide-react";
import { api, faDigits, faNum, fmtDate } from "../lib/api";
import { copyText } from "../lib/copy";
import { Pagination } from "./Inquiries";

const PAGE_SIZE = 20;

const EMPTY_FILTERS = {
  national_code: "",
  birth_date: "",
  first_name: "",
  last_name: "",
  father_name: "",
  register_no: "",
  register_place: "",
  gender: "",
  alive: "",
  updated_at: "",
  mobile_count: "",
};

// birth_date is stored as digits (13700512) or with slashes; show it as 1370/05/12
function formatBirth(bd) {
  const d = String(bd || "").replace(/\D/g, "");
  if (d.length !== 8) return bd ? faDigits(bd) : "-";
  return faDigits(`${d.slice(0, 4)}/${d.slice(4, 6)}/${d.slice(6)}`);
}

// ASCII version used for copying
function plainBirth(bd) {
  const d = String(bd || "").replace(/\D/g, "");
  if (d.length !== 8) return String(bd || "");
  return `${d.slice(0, 4)}/${d.slice(4, 6)}/${d.slice(6)}`;
}

const COLUMNS = [
  { key: "national_code", label: "کد ملی", placeholder: "مثلا 157", inputMode: "numeric" },
  { key: "birth_date", label: "تاریخ تولد", placeholder: "مثلا 1370 یا 1370/05", inputMode: "numeric" },
  { key: "first_name", label: "نام", placeholder: "جستجو" },
  { key: "last_name", label: "نام خانوادگی", placeholder: "جستجو" },
  { key: "father_name", label: "نام پدر", placeholder: "جستجو" },
  { key: "register_no", label: "شماره شناسنامه", placeholder: "مثلا 12", inputMode: "numeric" },
  { key: "register_place", label: "محل صدور", placeholder: "جستجو" },
  { key: "gender", label: "جنسیت", select: [["", "همه"], ["M", "مرد"], ["F", "زن"]] },
  { key: "alive", label: "وضعیت حیات", select: [["", "همه"], ["1", "زنده"], ["0", "فوت شده"]] },
  { key: "updated_at", label: "بروزرسانی", placeholder: "مثلا 1405/07", inputMode: "numeric" },
  { key: "mobile_count", label: "تعداد شماره موبایل", placeholder: "مثلا 2", inputMode: "numeric" },
  { key: "actions", label: "عملیات", noFilter: true },
];

const inputCls =
  "w-full min-w-[90px] px-2 py-1.5 rounded-lg border border-slate-300 focus:border-teal-500 outline-none text-xs font-normal bg-white";

export default function IdentityCache() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [loading, setLoading] = useState(false);
  const [onlineBusy, setOnlineBusy] = useState({});

  // top scrollbar that mirrors the table's horizontal scroll
  const tableWrapRef = useRef(null);
  const topBarRef = useRef(null);
  const [scrollW, setScrollW] = useState(0);
  const [clientW, setClientW] = useState(0);
  const syncing = useRef(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      Object.entries(appliedFilters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const { data } = await api.get("/admin/identities", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("خطا در دریافت کش هویت‌ها");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [page, appliedFilters]);

  // debounce column filter typing
  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedFilters(filters);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [filters]);

  // keep the top scrollbar width in sync with the table
  useEffect(() => {
    const el = tableWrapRef.current;
    if (!el) return;
    const measure = () => {
      setScrollW(el.scrollWidth);
      setClientW(el.clientWidth);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const tbl = el.querySelector("table");
    if (tbl) ro.observe(tbl);
    return () => ro.disconnect();
  }, [items.length, loading]);

  const syncScroll = (from, to) => {
    if (syncing.current) return;
    syncing.current = true;
    to.current.scrollLeft = from.current.scrollLeft;
    requestAnimationFrame(() => {
      syncing.current = false;
    });
  };

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  const hasFilters = Object.values(filters).some((v) => v);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const overflowing = scrollW > clientW + 1;

  const remove = async (nc) => {
    if (!window.confirm(`حذف کش ${nc}؟`)) return;
    await api.delete(`/admin/identities/${nc}`);
    toast.success("حذف شد");
    load();
  };

  const removeAll = async () => {
    if (!window.confirm("پاکسازی کل جدول کش هویت‌ها؟")) return;
    await api.delete("/admin/identities");
    toast.success("پاک شد");
    load();
  };

  const onlineInquiry = async (nc) => {
    setOnlineBusy((s) => ({ ...s, [nc]: true }));
    try {
      const { data } = await api.post(`/admin/identities/${nc}/online-inquiry`);
      if (data.status === "found") {
        if (data.updated) {
          toast.success(
            `استعلام آنلاین موفق — کش به‌روزرسانی شد (فیلدهای تغییریافته: ${data.changed_fields.join("، ")})`
          );
        } else {
          toast.success("استعلام آنلاین موفق — اطلاعات کش با ثبت احوال مطابقت دارد");
        }
        load();
      } else {
        toast.error(data.message || `استعلام ناموفق (${data.status})`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "خطا در استعلام آنلاین");
    } finally {
      setOnlineBusy((s) => ({ ...s, [nc]: false }));
    }
  };

  return (
    <div data-testid="identity-cache-page" className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Database className="text-teal-600" /> کش هویت‌ها
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            استعلامات موفق ذخیره‌شده — مجموع <b className="tabular-nums">{faNum(total)}</b> رکورد
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="identity-delete-all-button"
            onClick={removeAll}
            className="bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 px-4 py-2.5 rounded-xl flex items-center gap-2"
          >
            <Trash2 size={16} /> پاکسازی همه
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 text-xs text-slate-500">
          <span>
            برای هر ستون فیلتر بگذارید. کد ملی، تاریخ تولد، شماره شناسنامه و بروزرسانی با پیشوند جستجو می‌شوند. برای
            کپی، روی کد ملی یا تاریخ تولد کلیک کنید.
          </span>
          {hasFilters && (
            <button
              onClick={() => setFilters(EMPTY_FILTERS)}
              className="inline-flex items-center gap-1 text-rose-600 hover:bg-rose-50 px-2 py-1 rounded-lg"
            >
              <FilterX size={14} /> پاک کردن فیلترها
            </button>
          )}
        </div>

        {/* filters are in the table header; the scrollbar sits right above the table */}
        <div
          ref={topBarRef}
          onScroll={() => syncScroll(topBarRef, tableWrapRef)}
          data-testid="identity-top-scroll"
          className={`overflow-x-auto overflow-y-hidden bg-slate-50/80 border-b border-slate-100 ${
            overflowing ? "h-4" : "h-0"
          }`}
        >
          <div style={{ width: scrollW, height: 1 }} />
        </div>

        <div
          ref={tableWrapRef}
          onScroll={() => syncScroll(tableWrapRef, topBarRef)}
          className="overflow-x-auto"
        >
          <table className="w-full text-right text-sm">
            <thead className="bg-slate-50 text-slate-600 font-medium">
              <tr>
                {COLUMNS.map((c) => (
                  <th key={c.key} className="py-3 px-4 border-b whitespace-nowrap">
                    {c.label}
                  </th>
                ))}
              </tr>
              <tr>
                {COLUMNS.map((c) => (
                  <th key={`f-${c.key}`} className="py-2 px-3 border-b font-normal">
                    {c.noFilter ? null : c.select ? (
                      <select
                        data-testid={`identity-filter-${c.key}`}
                        value={filters[c.key]}
                        onChange={(e) => setFilter(c.key, e.target.value)}
                        className={inputCls}
                      >
                        {c.select.map(([v, l]) => (
                          <option key={v} value={v}>
                            {l}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="relative">
                        <input
                          data-testid={`identity-filter-${c.key}`}
                          type="text"
                          inputMode={c.inputMode}
                          placeholder={c.placeholder}
                          value={filters[c.key]}
                          onChange={(e) => setFilter(c.key, e.target.value)}
                          className={`${inputCls} pl-6`}
                        />
                        {filters[c.key] && (
                          <button
                            type="button"
                            onClick={() => setFilter(c.key, "")}
                            className="absolute left-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            title="پاک کردن"
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="p-8 text-center">
                    <Loader2 className="animate-spin inline" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="p-8 text-center text-slate-400">
                    کشی موجود نیست
                  </td>
                </tr>
              ) : (
                items.map((r, i) => (
                  <motion.tr
                    key={r.national_code}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.015 }}
                    className="row-hover border-b border-slate-100 last:border-0"
                  >
                    <td className="py-3 px-4 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => copyText(r.national_code, "کد ملی کپی شد")}
                        title="کلیک برای کپی"
                        className="tabular-nums font-medium hover:text-teal-700 hover:underline decoration-dotted underline-offset-4 cursor-copy"
                      >
                        {faDigits(r.national_code)}
                      </button>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => copyText(plainBirth(r.birth_date), "تاریخ تولد کپی شد")}
                        title="کلیک برای کپی"
                        className="tabular-nums hover:text-teal-700 hover:underline decoration-dotted underline-offset-4 cursor-copy"
                      >
                        {formatBirth(r.birth_date)}
                      </button>
                    </td>
                    <td className="py-3 px-4">{r.first_name || "-"}</td>
                    <td className="py-3 px-4">{r.last_name || "-"}</td>
                    <td className="py-3 px-4">{r.father_name || "-"}</td>
                    <td className="py-3 px-4 tabular-nums whitespace-nowrap">
                      {r.register_no ? faDigits(r.register_no) : "-"}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">{r.register_place || "-"}</td>
                    <td className="py-3 px-4 text-xs">{r.gender === "M" ? "مرد" : r.gender === "F" ? "زن" : "-"}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`text-xs px-2 py-1 rounded-full border whitespace-nowrap ${
                          r.alive
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-slate-100 text-slate-700 border-slate-200"
                        }`}
                      >
                        {r.alive ? "زنده" : "فوت شده"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-xs whitespace-nowrap">{fmtDate(Math.floor(r.updated_at / 1000))}</td>
                    <td className="py-3 px-4 tabular-nums">{faNum(r.mobile_count ?? 0)}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <button
                          data-testid={`online-inquiry-${r.national_code}`}
                          onClick={() => onlineInquiry(r.national_code)}
                          disabled={onlineBusy[r.national_code]}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 text-xs font-medium disabled:opacity-60 whitespace-nowrap"
                          title="استعلام آنلاین از ثبت احوال"
                        >
                          {onlineBusy[r.national_code] ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Radio size={14} />
                          )}
                          استعلام آنلاین
                        </button>
                        <button
                          data-testid={`delete-identity-${r.national_code}`}
                          onClick={() => remove(r.national_code)}
                          className="p-2 rounded-lg hover:bg-rose-50 text-rose-500"
                          title="حذف"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} onPage={setPage} testidPrefix="identity" />
      </div>
    </div>
  );
}
